import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Phone, MapPin, Building, Hash, Save, LogOut, Package, Heart, ChevronRight } from 'lucide-react';

const AccountPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    district: '',
    postal_code: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/giris', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${backendUrl}/api/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          district: data.district || '',
          postal_code: data.postal_code || ''
        });
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${backendUrl}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: profile.name,
          phone: profile.phone,
          address: profile.address,
          city: profile.city,
          district: profile.district,
          postal_code: profile.postal_code
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Profiliniz başarıyla güncellendi!' });
        setIsEditing(false);
      } else {
        throw new Error('Güncelleme başarısız');
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Bir hata oluştu. Lütfen tekrar deneyin.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen pt-32 pb-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-light tracking-wider mb-2">HESABIM</h1>
          <p className="text-gray-500">Hoş geldiniz, {profile.name || user?.name}</p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link 
            to="/siparislerim" 
            className="bg-white p-6 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between group"
            data-testid="account-orders-link"
          >
            <div className="flex items-center gap-4">
              <Package size={24} className="text-gray-400" />
              <div>
                <p className="font-medium">Siparişlerim</p>
                <p className="text-sm text-gray-500">Sipariş geçmişi</p>
              </div>
            </div>
            <ChevronRight size={20} className="text-gray-400 group-hover:translate-x-1 transition-transform" />
          </Link>

          <Link 
            to="/favoriler" 
            className="bg-white p-6 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between group"
            data-testid="account-favorites-link"
          >
            <div className="flex items-center gap-4">
              <Heart size={24} className="text-gray-400" />
              <div>
                <p className="font-medium">Favorilerim</p>
                <p className="text-sm text-gray-500">Beğendiğiniz ürünler</p>
              </div>
            </div>
            <ChevronRight size={20} className="text-gray-400 group-hover:translate-x-1 transition-transform" />
          </Link>

          <button 
            onClick={handleLogout}
            className="bg-white p-6 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between group text-left"
            data-testid="account-logout-button"
          >
            <div className="flex items-center gap-4">
              <LogOut size={24} className="text-gray-400" />
              <div>
                <p className="font-medium">Çıkış Yap</p>
                <p className="text-sm text-gray-500">Oturumu kapat</p>
              </div>
            </div>
            <ChevronRight size={20} className="text-gray-400 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Profile Form */}
        <div className="bg-white shadow-sm p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-medium">Kişisel Bilgiler</h2>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-sm text-black underline hover:no-underline"
                data-testid="edit-profile-button"
              >
                Düzenle
              </button>
            )}
          </div>

          {/* Message */}
          {message.text && (
            <div className={`mb-6 p-4 text-sm ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-6">
            {/* Name & Email Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-600 mb-2">Ad Soyad</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    name="name"
                    value={profile.name}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:outline-none focus:border-black text-sm disabled:bg-gray-50 disabled:text-gray-500"
                    data-testid="profile-name-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-2">E-posta</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="email"
                    name="email"
                    value={profile.email}
                    disabled
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 bg-gray-50 text-gray-500 text-sm cursor-not-allowed"
                    data-testid="profile-email-input"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">E-posta değiştirilemez</p>
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm text-gray-600 mb-2">Telefon</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="tel"
                  name="phone"
                  value={profile.phone}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="05XX XXX XX XX"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:outline-none focus:border-black text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  data-testid="profile-phone-input"
                />
              </div>
            </div>

            {/* Address Section */}
            <div className="pt-6 border-t">
              <h3 className="text-lg font-medium mb-4">Adres Bilgileri</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Adres</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 text-gray-400" size={18} />
                    <textarea
                      name="address"
                      value={profile.address}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      rows={2}
                      placeholder="Sokak, Cadde, Bina No, Daire No"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:outline-none focus:border-black text-sm disabled:bg-gray-50 disabled:text-gray-500 resize-none"
                      data-testid="profile-address-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">İl</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        name="city"
                        value={profile.city}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="İstanbul"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:outline-none focus:border-black text-sm disabled:bg-gray-50 disabled:text-gray-500"
                        data-testid="profile-city-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-2">İlçe</label>
                    <input
                      type="text"
                      name="district"
                      value={profile.district}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="Kadıköy"
                      className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:border-black text-sm disabled:bg-gray-50 disabled:text-gray-500"
                      data-testid="profile-district-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-2">Posta Kodu</label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        name="postal_code"
                        value={profile.postal_code}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="34000"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:outline-none focus:border-black text-sm disabled:bg-gray-50 disabled:text-gray-500"
                        data-testid="profile-postal-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 bg-black text-white py-3 text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="save-profile-button"
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Değişiklikleri Kaydet
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    fetchProfile(); // Reset to original values
                  }}
                  className="px-8 py-3 border border-gray-300 text-sm font-medium hover:bg-gray-50 transition-colors"
                  data-testid="cancel-edit-button"
                >
                  İptal
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Auth Type Info */}
        <div className="mt-4 text-center text-sm text-gray-500">
          {user?.auth_type === 'google' ? (
            <p>Google hesabı ile giriş yapıldı</p>
          ) : (
            <p>E-posta ile giriş yapıldı</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
